/**
 * Teabate-only: stance buttons and optional societal dimension.
 */
import React from "react";
import { CircleCheck, CircleX } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from "ui/InputSelect";

export interface AlignmentDimensionGroup {
  key: string;
  dimension: string;
  label1: string;
  label2: string;
}

export const TEABATE_ALIGNMENT_DIMENSIONS: AlignmentDimensionGroup[] = [
  { key: "nationalism_cosmopolitanism", dimension: "Diplomacy", label1: "Nationalism", label2: "Cosmopolitanism" },
  { key: "theocracy_secularism", dimension: "Religion", label1: "Theocracy", label2: "Secularism" },
  { key: "authority_liberty", dimension: "State", label1: "Authority", label2: "Liberty" },
  { key: "capitalism_socialism", dimension: "Economics", label1: "Capitalism", label2: "Socialism" },
  { key: "dogma_science", dimension: "Beliefs", label1: "Dogma", label2: "Science" },
  { key: "autocracy_democracy", dimension: "Government", label1: "Autocracy", label2: "Democracy" },
  { key: "conservatism_progressivism", dimension: "Traditions", label1: "Conservatism", label2: "Progressivism" },
  { key: "content_discontent", dimension: "Sentiment", label1: "Status Quo", label2: "Reformation" },
];

export interface TeabateFieldsProps {
  argumentType: "agree" | "disagree";
  onArgumentTypeChange: (v: "agree" | "disagree") => void;
  selectedAlignment: string;
  onAlignmentChange: (v: string) => void;
  requireAlignmentDimension?: boolean;
  dimensionError?: string | null;
}

const TeabateFields: React.FC<TeabateFieldsProps> = ({
  argumentType,
  onArgumentTypeChange,
  selectedAlignment,
  onAlignmentChange,
  requireAlignmentDimension = false,
  dimensionError = null,
}) => (
  <>
    <div className="space-y-2">
      <span className="block text-sm font-semibold">Your stance</span>
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => onArgumentTypeChange("disagree")}
          className={`px-2 py-1.5 text-xs rounded-full transition-colors flex items-center gap-1 ${
            argumentType === "disagree" ? "bg-red-600 text-white font-semibold" : "btn-secondary font-normal"
          }`}
        >
          <CircleX className="size-4" aria-hidden />
          Disagree
        </button>
        <button
          type="button"
          onClick={() => onArgumentTypeChange("agree")}
          className={`px-2 py-1.5 text-xs rounded-full transition-colors flex items-center gap-1 ${
            argumentType === "agree" ? "bg-green-600 text-white font-semibold" : "btn-secondary font-normal"
          }`}
        >
          <CircleCheck className="size-4" aria-hidden />
          Agree
        </button>
      </div>
    </div>

    <div className="space-y-2">
      <span className="block text-sm font-semibold mb-2">
        Users who agree tend to prefer
        {requireAlignmentDimension ? <span className="ml-0.5 text-warning-red">*</span> : null}
      </span>
      <Select value={selectedAlignment} onValueChange={onAlignmentChange}>
        <SelectTrigger className={`input-select ${dimensionError ? "border-warning-red dark:border-dark-warning-red" : ""}`}>
          <SelectValue placeholder="Select a societal dimension" />
        </SelectTrigger>
        <SelectContent className="sm:max-w-[400px] border border-eoe-border-main dark:border-eoe-dark-border-main">
          <SelectItem value="none">None</SelectItem>
          {TEABATE_ALIGNMENT_DIMENSIONS.map((group) => (
            <SelectGroup key={group.key}>
              <SelectLabel>{group.dimension}</SelectLabel>
              <SelectItem value={group.label1.toLowerCase()}>{group.label1}</SelectItem>
              <SelectItem value={group.label2.toLowerCase()}>{group.label2}</SelectItem>
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
      {dimensionError ? (
        <p className="text-sm text-warning-red dark:text-dark-warning-red">
          {dimensionError}
        </p>
      ) : null}
    </div>
  </>
);

export default TeabateFields;
